import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, Mail, Send, Facebook, Instagram, Youtube, Crown } from "lucide-react";
import { toast } from "sonner";
import { HOTEL } from "../data/site";

const INQUIRY_TYPES = [
  { id: "wedding", label: "Wedding / Mandap" },
  { id: "reception", label: "Reception" },
  { id: "engagement", label: "Engagement / Pooja" },
  { id: "birthday", label: "Birthday / Family Event" },
  { id: "stay", label: "Room / Stay Only" },
];

export default function Contact() {
  const [form, setForm] = useState({
    type: "wedding",
    name: "",
    phone: "",
    date: "",
    guests: "100-200",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please enter your name and phone number.");
      return;
    }
    setSubmitting(true);
    const typeLabel = INQUIRY_TYPES.find((t) => t.id === form.type)?.label || form.type;
    const isStay = form.type === "stay";
    const greeting = isStay
      ? "Namaste! I'd like to enquire about a room at Maa Durga Palace & Guest House."
      : `Namaste! I'd like to enquire about the ${typeLabel} venue at Maa Durga Palace, Bhadrak.`;

    const text = [
      greeting,
      ``,
      `Inquiry Type: ${typeLabel}`,
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      form.date ? `Event / Check-in Date: ${form.date}` : null,
      isStay ? `Guests: ${form.guests}` : `Expected Guest Count: ${form.guests}`,
      form.message ? `Message: ${form.message}` : null,
    ]
      .filter(Boolean)
      .join("\n");
    const url = `https://wa.me/91${HOTEL.whatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening WhatsApp — we'll respond shortly.");
    setTimeout(() => setSubmitting(false), 600);
  };

  const isStay = form.type === "stay";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding bg-background"
    >
      <div className="container-x">
        <div className="max-w-2xl">
          <span className="eyebrow flex items-center gap-2">
            <Crown size={12} /> Book & Enquire
          </span>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-foreground">
            Reserve your venue,<br />
            <span className="italic text-[#7A1A24] dark:text-[#D4AF37]">in a single message.</span>
          </h2>
          <p className="mt-5 text-muted-foreground">
            Tell us what you're celebrating and we'll continue the conversation on
            WhatsApp — usually within minutes.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <motion.form
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 glass-card rounded-3xl p-6 sm:p-8 space-y-5"
            data-testid="enquiry-form"
          >
            <Field label="Inquiry Type">
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Inquiry type">
                {INQUIRY_TYPES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={form.type === t.id}
                    onClick={() => setForm((f) => ({ ...f, type: t.id }))}
                    data-testid={`inquiry-type-${t.id}`}
                    className={`px-4 py-2 rounded-full text-xs font-medium tracking-wide border transition-all ${
                      form.type === t.id
                        ? "bg-[#7A1A24] text-white border-[#7A1A24] shadow-md shadow-[#7A1A24]/25"
                        : "bg-card text-muted-foreground border-border hover:border-[#D4AF37]/60 hover:text-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Your Name *">
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Subhasish Mohanty"
                  data-testid="form-name"
                  className="input-field"
                />
              </Field>
              <Field label="Phone Number *">
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="+91 98765 43210"
                  data-testid="form-phone"
                  className="input-field"
                />
              </Field>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={isStay ? "Check-in Date" : "Event Date"}>
                <input
                  type="date"
                  value={form.date}
                  onChange={update("date")}
                  data-testid="form-date"
                  className="input-field"
                />
              </Field>
              <Field label={isStay ? "Number of Guests" : "Expected Guest Count"}>
                <select
                  value={form.guests}
                  onChange={update("guests")}
                  data-testid="form-guests"
                  className="input-field"
                >
                  {isStay ? (
                    <>
                      <option value="1">1 Guest</option>
                      <option value="2">2 Guests</option>
                      <option value="3">3 Guests</option>
                      <option value="4">4 Guests</option>
                      <option value="5+">5 or more</option>
                    </>
                  ) : (
                    <>
                      <option value="50-100">50 – 100 guests</option>
                      <option value="100-200">100 – 200 guests</option>
                      <option value="200-300">200 – 300 guests</option>
                      <option value="300-500">300 – 500 guests</option>
                      <option value="500+">500+ guests</option>
                    </>
                  )}
                </select>
              </Field>
            </div>

            <Field label="Message (optional)">
              <textarea
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder={
                  isStay
                    ? "Any preferences — room type, special requests, expected arrival time…"
                    : "Tell us about the event — ceremony type, decoration preferences, catering, dates…"
                }
                data-testid="form-message"
                className="input-field resize-none"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="form-submit"
              className="btn-whatsapp w-full sm:w-auto"
            >
              <Send size={16} />
              {submitting ? "Opening WhatsApp…" : "Send on WhatsApp"}
            </button>

            <p className="text-xs text-muted-foreground">
              By submitting, you'll be redirected to WhatsApp with your enquiry pre-filled.
              No data is stored on this site.
            </p>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="lg:col-span-5 flex flex-col gap-4"
          >
            <a
              href={`tel:${HOTEL.phoneE164}`}
              data-testid="contact-call"
              className="group rounded-3xl border border-border bg-card p-6 flex items-center gap-4 hover:border-[#D4AF37] transition-colors"
            >
              <div className="h-12 w-12 rounded-xl bg-[#7A1A24]/10 dark:bg-[#D4AF37]/10 text-[#7A1A24] dark:text-[#D4AF37] grid place-items-center group-hover:scale-110 transition-transform">
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Call us</div>
                <div className="font-display text-xl text-foreground">{HOTEL.phoneFormatted}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Available 24/7</div>
              </div>
            </a>

            <a
              href={HOTEL.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="group rounded-3xl border border-border bg-card p-6 flex items-center gap-4 hover:border-[#25D366] transition-colors"
            >
              <div className="h-12 w-12 rounded-xl bg-[#25D366]/15 text-[#1FB855] grid place-items-center group-hover:scale-110 transition-transform">
                <MessageCircle size={20} />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">WhatsApp</div>
                <div className="font-display text-xl text-foreground">{HOTEL.phoneFormatted}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Fast bookings & replies</div>
              </div>
            </a>

            <a
              href={`mailto:bookings@maadurgapalace.in?subject=Wedding%20%2F%20Venue%20Enquiry%20—%20Maa%20Durga%20Palace`}
              data-testid="contact-email"
              className="group rounded-3xl border border-border bg-card p-6 flex items-center gap-4 hover:border-[#D4AF37] transition-colors"
            >
              <div className="h-12 w-12 rounded-xl bg-[#D4AF37]/15 text-[#A8862B] dark:text-[#E6C555] grid place-items-center group-hover:scale-110 transition-transform">
                <Mail size={20} />
              </div>
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email</div>
                <div className="font-display text-lg text-foreground">bookings@maadurgapalace.in</div>
                <div className="text-xs text-muted-foreground mt-0.5">Reply within 24 hours</div>
              </div>
            </a>

            <div className="rounded-3xl border border-border bg-card p-6">
              <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground">
                Follow our journey
              </div>
              <div className="mt-3 flex gap-2">
                {[
                  { Icon: Facebook, href: "#", label: "Facebook" },
                  { Icon: Instagram, href: "#", label: "Instagram" },
                  { Icon: Youtube, href: "#", label: "YouTube" },
                ].map(({ Icon, href, label }) => (
                  <a
                    key={label}
                    href={href}
                    aria-label={label}
                    className="h-10 w-10 rounded-full grid place-items-center border border-border hover:bg-foreground/5 hover:border-[#D4AF37] text-foreground transition-all"
                  >
                    <Icon size={16} />
                  </a>
                ))}
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
